<template>
    <Modal small ref="modal" qa-name="feedback_modal">
        <div class="flex flex-col gap-6">
            <h1 class="font-medium text-lg">Trouble with your order? Tell us how we can help.</h1>
            <p class="text-sm" v-if="!submitted && !submitting">Share your feedback in the form below. Our team reviews every comment. If you need immediate help, contact Keap Sales at <a href="tel:18668000004" class="underline font-medium">+1 (866) 800-0004</a>.</p>
            <Spinner class="w-[80px] h-[80px] mx-auto" v-if="submitting"/>
            <p class="text-sm" v-if="error">Something went wrong when submitting your feedback. Please try again.</p>
            <form class="w-full mb-6" @submit.prevent="submitFeedback()" v-if="!submitting && !submitted">
                <div class="field-grid mb-6">
                    <InputField
                        key="firstName"
                        v-model="firstName"
                        :value="firstName"
                        data-qa="firstName-field"
                        :valid="firstNameValid"
                        field-label="First name"
                        field-type="text"
                        required
                        :readonly="$store.getters.isUpgrading"
                        class="col-span-2"
                    >
                        <template v-slot:error>
                            <p v-text="firstNameError"></p>
                        </template>
                    </InputField>
                    <InputField
                        key="lastName"
                        v-model="lastName"
                        :value="lastName"
                        data-qa="lastName-field"
                        :valid="lastNameValid"
                        field-label="Last name"
                        field-type="text"
                        required
                        :readonly="$store.getters.isUpgrading"
                        class="col-span-2"
                    >
                        <template v-slot:error>
                            <p v-text="lastNameError"></p>
                        </template>
                    </InputField>
                    <InputField
                        key="email"
                        v-model="email"
                        :value="email"
                        data-qa="email-field"
                        :valid="emailValid"
                        field-label="Email"
                        field-type="text"
                        required
                        :readonly="$store.getters.isUpgrading"
                        class="col-span-2"
                    >
                        <template v-slot:error>
                            <p v-text="emailError"></p>
                        </template>
                    </InputField>
                    <Textarea
                        key="feedback-field"
                        v-model="feedback"
                        :value="feedback"
                        data-qa="feedback-field"
                        :valid="feedbackValid"
                        field-label="Feedback"
                        field-type="text"
                        required
                        class="col-span-2"
                    >
                        <template v-slot:error>
                            <p v-text="feedbackError"></p>
                        </template>
                    </Textarea>
                </div>
                <div class="w-full flex gap-6">
                    <KeapButton xs data-qa="submit-feedback-button" :disabled="!canSubmit" @click.prevent="submitFeedback()">Submit feedback</KeapButton>
                    <Link class="font-normal" no-underline @click="closeModal()" data-qa="submit-feedback-cancel-button">Cancel</Link>
                </div>
            </form>
            <p class="text-sm" v-if="submitted">Thank you for telling us about your experience.</p>
        </div>
    </Modal>
</template>

<script>
import Modal from '@/Components/Modal.vue';
import Spinner from '@/BuyNow/Components/Spinner.vue';
import KeapButton from '@/Components/KeapButton.vue';
import Link from '@/Components/Link.vue';
import Textarea from '@/BuyNow/Components/inputs/Textarea.vue';
import InputField from '@/BuyNow/Components/inputs/InputField.vue';
import { EMAIL_REGEX, SUPPORT_PHONE_NUMBER } from '@/Util/constants.js';

export default {
    components: {
        Modal,
        Link,
        KeapButton,
        InputField,
        Spinner,
        Textarea,
    },
    props: {
        noClose: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            feedback: '',
            feedbackValid: false,
            feedbackError: '',
            submitting: false,
            submitted: false,
            error: false,
        }
    },
    expose: [
        'openModal'
    ],
    methods: {
        async submitFeedback() {
            this.submitting = true;
            this.$nextTick(() => {
                this.$refs.modal.resize();
            })
            await window.Sentry.setUser(this.email);
            const status = await this.$store.dispatch('submitFeedback', this.feedback);
            if (status) {
                this.submitting = false;
                this.submitted = true;
            } else {
                this.submitting = false;
                this.error = true;
            }
            this.$refs.modal.resize();
        },
        openModal() {
            this.$refs.modal.openModal();
        },
        closeModal() {
            this.$refs.modal.closeModal();
        }
    },
    computed: {
        canSubmit() {
            return this.firstNameValid && this.lastNameValid && this.emailValid && this.feedbackValid;
        },
        firstName: {
            get() {
                return this.$store.state.userInfo.firstName;
            },
            set(value) {
                this.$store.commit('updateUserInfo', { firstName: value });
            }
        },
        firstNameValid: {
            get() {
                return this.$store.state.validation.firstName.valid;
            },
            set(value) {;
                this.$store.commit('updateValidation', { firstName: { valid: value, message: this.firstNameError }});
            }
        },
        firstNameError: {
            get() {
                return this.$store.state.validation.firstName.message;
            },
            set(value) {
                this.$store.commit('updateValidation', { firstName: { valid: this.firstNameValid, message: value }});
            }
        },
        lastName: {
            get() {
                return this.$store.state.userInfo.lastName;
            },
            set(value) {
                this.$store.commit('updateUserInfo', { lastName: value });
            }
        },
        lastNameValid: {
            get() {
                return this.$store.state.validation.lastName.valid;
            },
            set(value) {
                this.$store.commit('updateValidation', { lastName: { valid: value, message: this.lastNameError }});
            }
        },
        lastNameError: {
            get() {
                return this.$store.state.validation.lastName.message;
            },
            set(value) {
                this.$store.commit('updateValidation', { lastName: { valid: this.lastNameValid, message: value }});
            }
        },
        email: {
            get() {
                return this.$store.state.userInfo.email;
            },
            set(value) {
                this.$store.commit('updateUserInfo', { email: value });
            }
        },
        emailValid: {
            get() {
                return this.$store.state.validation.email.valid;
            },
            set(value) {
                this.$store.commit('updateValidation', { email: { valid: value, message: this.emailError }});
            }
        },
        emailError: {
            get() {
                return this.$store.state.validation.email.message;
            },
            set(value) {
                this.$store.commit('updateValidation', { email: { valid: this.emailValid, message: value }});
            }
        },
        supportNumber() {
            return `tel:${SUPPORT_PHONE_NUMBER}`;
        },
        supportNumberDisplay() {
            return SUPPORT_PHONE_NUMBER;
        },
    },
    watch: {
        // Form validation
        firstName(newVal, oldVal) {
            if (newVal == '') {
                this.firstNameValid = false;
                this.firstNameError = 'First name is required';
            } else {
                this.firstNameValid = true;
            }
        },
        lastName(newVal, oldVal) {
            if (newVal == '') {
                this.lastNameValid = false;
                this.lastNameError = 'Last name is required';
            } else {
                this.lastNameValid = true;
            }
        },
        feedback(newVal, oldVal) {
            if (newVal == '') {
                this.feedbackValid = false;
                this.feedbackError = 'Feedback is required';
            } else {
                this.feedbackValid = true;
            }
        },
        email(newVal, oldVal) {
            if (newVal == '') {
                this.emailValid = false;
                this.emailError = 'Email is required';
            } else if (!RegExp(EMAIL_REGEX).test(newVal)) {
                this.emailValid = false;
                this.emailError = 'Email is not valid';
            } else {
                this.emailValid = true;
            }
        },
    }
};
</script>

<style lang="scss" scoped>
</style>
